import config from './appConfig.json';

/* eslint-disable no-undef */

/**
 * Deployment configuration: the committed JSON, with per-region overrides applied at build time.
 *
 * `appConfig.json` describes the US deployment. A second region overrides only the fields that
 * differ, via REACT_APP_* variables set by its deploy wrapper — the same pattern the Firebase
 * config and the backend's CORS origins use. Nothing here is region-specific; the values are.
 *
 * This layer previously did not exist: appConfig.js simply re-exported the JSON, even though
 * ConfigContext described it as "the JavaScript config that processes environment variables".
 * The consequence was not cosmetic. `config.market` drives which signup tabs Login.js enables,
 * so an India deployment inherited `market: "US"` and offered users the US signup while
 * disabling India's — the precise opposite of what that region should allow. It also served
 * the US site name, company name, default ticker and ticker list to India customers.
 *
 * An unset variable leaves the JSON value untouched, so the US build is byte-for-byte unchanged.
 */
const OVERRIDES = {
  market: process.env.REACT_APP_MARKET,
  // THE CHART VIEW THIS DEPLOYMENT SERVES: 'long' or 'short'.
  //
  // Enforced for everyone except an admin. A non-admin has no toggle at all, so whichever value a
  // region ships is the view its users get - which is the point: the preset decides what the
  // product is called and which studies are reachable, and that is a deployment decision rather
  // than a per-browser preference.
  barMode: process.env.REACT_APP_BAR_MODE,
  siteName: process.env.REACT_APP_SITE_NAME,
  companyName: process.env.REACT_APP_COMPANY_NAME,
  websiteUrl: process.env.REACT_APP_WEBSITE_URL,
  defaultTicker: process.env.REACT_APP_DEFAULT_TICKER,
  tickerListName: process.env.REACT_APP_TICKER_LIST_NAME,
  // Where the precomputed TrendPulse and CTS datasets are served from. A second region points
  // at its own bucket; an unset variable leaves the US value untouched, as with every field here.
  precomputedBaseUrl: process.env.REACT_APP_PRECOMPUTED_BASE_URL,
  // STRIPE'S PUBLISHABLE key - not the secret one, which lives in Secret Manager and never reaches
  // a browser. It is here because it was hardcoded in two components, identically, as a `pk_test_`
  // key: production has been serving a TEST-mode key, so a real customer reaching checkout gets
  // Stripe's test environment and is charged nothing. Settable per deployment now rather than
  // needing a code change, and a second region needs its own account's key anyway.
  stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
};

/* eslint-enable no-undef */

const resolved = { ...config };
Object.keys(OVERRIDES).forEach((key) => {
  const value = OVERRIDES[key];
  // Only a non-empty string counts as an override. CRA inlines an unset REACT_APP_* as
  // undefined, but a wrapper that exports the variable empty would otherwise blank the field.
  if (typeof value === 'string' && value.trim() !== '') {
    resolved[key] = value.trim();
  }
});

export default resolved;
